import BigNumber from "bignumber.js";
import { exactPaySwap, ISwap, ISwapPathInfo } from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "../../../../utils/helper";
import { toQuoteDetailsAmount } from "../Content/components/QuoteDetails";
import { getApiSwapPath } from "../Content/helpers/getApiSwapPath";
import { useCreateSwapRequestContext } from "../context";
import { useSwap } from "./useSwap";
import { getCommonDisplayDecimals } from "../helpers/getCommonDisplayDecimals";
import { getTronSmartAccountType } from "../../helpers/getTronSmartAccountType";

interface IProps {
  swapPaths: ISwapPathInfo[];
}

export const useExactPaySwap = (props: IProps) => {
  const { swapPaths } = props;

  const { filterFields, exactPayFields, setExactPayFields, resetQuoteDetails } =
    useCreateSwapRequestContext();

  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const { selectChainByPieces } = useChains();
  const { merchantId } = useAppSelector(selectProfile);
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const fromCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.fromCurrency
      )
    ) || 6;

  const toCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.toCurrency
      )
    ) || 6;

  const validateExactPaySwap = (): boolean => {
    if (!filterFields.fromCurrency || !filterFields.toCurrency) {
      alerting("warning", t("psCurrencyPair"));
      return false;
    }

    if (!exactPayFields.walletAddress) {
      alerting("warning", t("psWalletAddress"));
      return false;
    }

    if (!exactPayFields.exactPayAmount) {
      alerting("warning", t("peExactPayAmount"));
      return false;
    }

    if (!exactPayFields.estimateAmount || !exactPayFields.getMin) {
      alerting("warning", t("plzQuoteFist"));
      return false;
    }

    return true;
  };

  const swapPayload: ISwap = {
    chain_type: Number(chain_type),
    chain_id: Number(chain_id),
    asset_name: filterFields.fromCurrency,
    asset_name_get: filterFields.toCurrency,
    smart_account_type: getTronSmartAccountType(
      Number(chain_type),
      filterFields.isTronRent
    ),
    orders: [
      {
        // use decimals
        amount: amountToCryptoAmount(
          BigNumber(exactPayFields.exactPayAmount || "")
            .decimalPlaces(getCommonDisplayDecimals(fromCurrencyDecimals), 1)
            .toFixed(),
          fromCurrencyDecimals
        ),
        decimals: fromCurrencyDecimals,

        from_address: exactPayFields.walletAddress,
        to_address: exactPayFields.walletAddress,

        decimals_get: toCurrencyDecimals,
        amount_get_est: amountToCryptoAmount(
          toQuoteDetailsAmount(
            exactPayFields.estimateAmount,
            toCurrencyDecimals
          ),
          toCurrencyDecimals
        ),
        amount_get_min: amountToCryptoAmount(
          toQuoteDetailsAmount(exactPayFields.getMin, toCurrencyDecimals),
          toCurrencyDecimals
        ),

        max_slippage: Number(filterFields.maxSlippage) * 100000,
        swap_path: getApiSwapPath(
          swapPaths,
          filterFields.fromCurrency,
          filterFields.toCurrency
        ),
      },
    ],
  };

  const { handleSwap } = useSwap({
    resetAmount: () => {
      setExactPayFields({
        ...exactPayFields,
        exactPayAmount: "",
      });
      resetQuoteDetails();
    },
    validateFields: validateExactPaySwap,
    swapApiFunc: exactPaySwap,
    swapPayload: swapPayload,
  });

  return { handleSwap };
};
